import * as PIXIFILTERS from 'pixi-filters'
import { Matrix } from 'pixi.js';

import {
    game
} from "../Game"
import { Settings } from '../Settings';
import {
	addScreenFilter,
	initScreenFilterTarget,
	removeScreenFilter,
} from './ScreenFilterStack.js';

let currentEffects = [];
let effectTarget = null;

export const init = (target) =>{
	effectTarget = target;
	initScreenFilterTarget(target);
}
export const update = () => {
	// Shake moves the scene itself, so apply it before positioning filters.
	// Snapshot the list because expired effects remove themselves while updating.
	const effects = [...currentEffects];
	for(const effect of effects) if(effect.type === effectTypes.screenShake) effect.update();
	for(const effect of effects) if(effect.type !== effectTypes.screenShake) effect.update();
}
export const effectTypes = {
	screenShake:'screenShake',
	shockWave:'shockWave',
}
export const addEffect = (type, props) =>{
	let effect = null;
	const effectMargin = 300;
	const follow = game.editor.container;
	// Input points are level pixels. toGlobal already includes the camera;
	// retain the original position so camera motion never accumulates in it.
	const screenPoint = props.point && follow.toGlobal(props.point);

	if(screenPoint){
		if(screenPoint.x > window.innerWidth + effectMargin || screenPoint.x < -effectMargin
			|| screenPoint.y > window.innerHeight + effectMargin || screenPoint.y < -effectMargin){
				return;
		}
	}

	switch(type){
		case effectTypes.shockWave:
			if(currentEffects.filter(effect => effect.type === effectTypes.shockWave).length >= Settings.maxShockwaveEffects) return;
			const shockFilter = new PIXIFILTERS.ShockwaveFilter({
				center: {x: screenPoint.x, y: screenPoint.y},
				amplitude: 20,
				wavelength: 351,
				brightness: 1,
				radius: props.radius,
			})
			effect = new PixiEffect(shockFilter, type, {...props, point: {x: props.point.x, y: props.point.y}, follow, maxLife:600})
		break;
		case effectTypes.screenShake:
			const centerDistanceX = window.innerWidth / 2 - screenPoint.x;
			const centerDistanceY = window.innerHeight / 2 - screenPoint.y;
			const centerDistance = Math.sqrt(centerDistanceX * centerDistanceX + centerDistanceY * centerDistanceY);

			const maxLengthX = window.innerWidth / 2 + effectMargin / 2;
			const maxLengthY = window.innerHeight / 2 + effectMargin / 2;
			const maxLength = Math.sqrt(maxLengthX * maxLengthX + maxLengthY * maxLengthY);

			const effectDegration = 1 - (centerDistance / maxLength);

			const amplitude = Math.max(0, props.amplitude * effectDegration);
			const activeScreenShake = currentEffects.find(effect => effect.type === effectTypes.screenShake);
			if(activeScreenShake){
				activeScreenShake.addImpulse(amplitude);
				return activeScreenShake;
			}
			effect = new ScreenShakeEffect(amplitude, 500, 60);
		break
	}
	if(effect) currentEffects.push(effect);
}
export const removeEffect = effect =>{
	for(let i = 0; i<currentEffects.length; i++){
		if(currentEffects[i] === effect){
			currentEffects.splice(i, 1);
			effect.remove(effect.filter);
			break;
		}
	}
}

class BaseEffect{
	constructor(){}
	add(){}
	remove(){}
	update(){}
}

class PixiEffect extends BaseEffect{
	constructor(filter, type, props){
		super();
		this.props = props;
		this.filter = filter;
		this.type = type;
		this.startTime = Date.now();
		this.followTransform = new Matrix();
		this.update();
		this.add(filter);
	}
	add(pixiFilter){
		addScreenFilter(pixiFilter);
	}
	remove(pixiFilter){
		removeScreenFilter(pixiFilter);
	}
	update(){
		if(this.type == effectTypes.shockWave){
			this.filter.time = ((Date.now()-this.startTime)/1000*2)%2.5;

			// The camera is a parent of follow. Reproject the fixed level point
			// using its current transform, including zoom-only changes.
			const transform = this.props.follow.getGlobalTransform(this.followTransform);
			transform.apply(this.props.point, this.filter.center);
			const scale = Math.hypot(transform.a, transform.b);
			this.filter.radius = this.props.radius*scale;
			this.filter.wavelength = 351*scale;
			this.filter.amplitude = 20*scale;
			this.filter.speed = 500*scale;

		}
		if(this.props.maxLife && Date.now() > this.startTime+this.props.maxLife){
			removeEffect(this);
		}
	}
}
class ScreenShakeEffect extends BaseEffect{
	constructor(amplitude, duration, frequency){
		super();
		this.type = effectTypes.screenShake;
		this.amplitude = amplitude;
		this.duration = duration;
		this.frequency = frequency;
		this.startTime = Date.now();
		this.t = 0;
		this.add();
	}
	addImpulse(amplitude){
		const remainingAmplitude = this.amplitude * this.decay();
		this.amplitude = Math.max(remainingAmplitude, amplitude);
		this.startTime = Date.now();
		this.t = 0;
	}
	add(){
		const sampleCount = (this.duration/1000) * this.frequency;
		this.samplesx = [];
		this.samplesy = [];
		for(let i = 0; i < sampleCount; i++) {
			this.samplesx.push(Math.random() * 2 - 1);
			this.samplesy.push(Math.random() * 2 - 1);
		}
	}
	strength(samples){
		const s = this.t / 1000 * this.frequency;
		const s0 = Math.floor(s);
		const s1 = s0 + 1;
		const k = this.decay();
		return (this.noise(s0, samples) + (s - s0)*(this.noise(s1, samples) - this.noise(s0, samples))) * k;
	};
	update(){
		this.t = Date.now() - this.startTime;
		if(this.t > this.duration){
			removeEffect(this);
			return;
		}
		effectTarget.x = this.strength(this.samplesx)*this.amplitude;
		effectTarget.y = this.strength(this.samplesy)*this.amplitude;
	};
	remove(){
		effectTarget.x = 0;
		effectTarget.y = 0;
	}
	decay(){
		if(this.t >= this.duration) return 0;
		return (this.duration - this.t) / this.duration;
	}
	noise(s, samples)
	{
		if(s >= samples.length) return 0;
		return samples[s];
	};
}
